import type { ModelProvider } from './model-provider-secrets.types.js';
import type { UserCredentialKind } from './secret.types.js';

/**
 * Encoding of the `name` a subscription credential is mirrored under in
 * `model_provider_secrets` (`mps_*`). The server writes the mirror row when a
 * subscription is registered; the web key pickers read it back. Both sides need
 * the same encoding — the server to find the credential behind a pinned row,
 * the client to know which credential a picked row would update — so the pair
 * of functions lives here rather than in either consumer.
 */

/**
 * Human-facing product name stored as the `name` of a subscription mirror row,
 * keyed by credential kind. Surfaces in the key pickers so a subscription reads
 * as "Claude Max" / "Codex" rather than the internal alias it is keyed under.
 */
export const SUBSCRIPTION_PRODUCT_NAMES: Record<UserCredentialKind, string> = {
  'claude-max': 'Claude Max',
  'codex-subscription': 'Codex',
};

/**
 * The subscription kind each model provider authenticates with, for the
 * providers that have one. Lets a surface holding only a mirror row (which
 * carries a provider, not a kind) reach the product name and the alias
 * encoding above.
 */
export const SUBSCRIPTION_KIND_BY_PROVIDER: Partial<Record<ModelProvider, UserCredentialKind>> = {
  anthropic: 'claude-max',
  openai: 'codex-subscription',
};

/**
 * Stored `name` for a subscription mirror row. The `default` alias uses the
 * bare product name; a non-default alias is suffixed so multiple subscriptions
 * of the same kind stay distinct and keep a stable upsert key on
 * `(scope, org, owner, provider, name)`.
 */
export function subscriptionMirrorName(kind: UserCredentialKind, alias: string): string {
  const base = SUBSCRIPTION_PRODUCT_NAMES[kind];
  return alias === 'default' ? base : `${base} (${alias})`;
}

/**
 * Inverse of {@link subscriptionMirrorName}: recover the alias a subscription
 * mirror row was registered under from its stored `name`. The bare product name
 * maps back to `'default'`; a `"<Product> (x)"` suffix recovers alias `x`.
 *
 * A name matching neither product shape is a **legacy alias-named row** —
 * before the product-name rename, mirror rows were stored with `name === alias`
 * (the bare alias, e.g. `'work'`). Those round-trip by treating the whole name
 * AS the alias. We must NOT collapse them to `'default'`: a pinned non-default
 * legacy row (`name: 'work'`) would then refresh/mount `CLAUDE_MAX_DEFAULT` and
 * hand the box the wrong Claude account. Returning the name fails safe instead —
 * if it isn't a real alias, the credential lookup finds no row and reports the
 * credential missing rather than silently substituting another account.
 */
export function subscriptionMirrorAlias(kind: UserCredentialKind, name: string): string {
  const base = SUBSCRIPTION_PRODUCT_NAMES[kind];
  if (name === base) return 'default';
  const prefix = `${base} (`;
  if (name.startsWith(prefix) && name.endsWith(')')) {
    const inner = name.slice(prefix.length, -1);
    if (inner.length > 0) return inner;
  }
  // Legacy alias-named row: the stored `name` is the bare alias itself.
  return name;
}
